import React, { Fragment } from "react";

import Button from "@common/components/Button/Button";
import Input from "@common/components/Input/Input";
import { ModalProps } from "@common/components/Modal/Modal";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { getIntegrationTypeName, IntegrationLayoutEntry, IntegrationType, Slack } from "@common/models/integrations";
import { vars } from "@common/styles";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { IntegrationHelperText } from "./components/IntegrationHelperText";
import { useSlackIntegrationFormConsumer } from "./consumers/useSlackIntegrationFormConsumer";

interface SlackIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Slack;
  selectedAppId: string;
  onFinish: () => void;
}

export const SlackIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: SlackIntegrationModalProps) => {
  const {
    formState,
    formError,
    handleFormChange,
    handleFormSubmit,
    disabled,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useSlackIntegrationFormConsumer({
    selectedAppId,
    onFinish,
    integration,
  });

  const { webhook, channel } = formState;
  const { webhook: webhookErr, channel: channelErr } = formError;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    handleFormChange(e.currentTarget.name, e.currentTarget.value);
  }

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(webhook, channel, integrationLayout, integration) && !hasMetadataKeyChanges;
    const tooltipText = !webhook || !channel ? "Please enter webhook and channel first" : "No changes have been made";

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: tooltipText,
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || disabled}
        loadingText={disabled && (integration ? "Saving..." : "Connecting...")}
        onClick={handleFormSubmit}
      >
        {integration ? "Save changes" : "Connect"}
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.SLACK}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.SLACK)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.SLACK)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <IntegrationHelperText
          arrowColor={vars.colors.slackYellow}
          titleComponent={
            <Fragment>
              Click this link to&nbsp;
              <a
                href="https://my.slack.com/services/new/incoming-webhook"
                target="_blank"
                rel="noreferrer"
              >
                Add Incoming WebHook
              </a>
              &nbsp;on Slack
            </Fragment>
          }
          textElements={[
            <Paragraph key="1">Choose a channel to forward tickets into</Paragraph>,
            <Paragraph key="2">Click Add Incoming WebHooks integration button</Paragraph>,
            <Paragraph key="3">Paste the generated webhook URL and the channel name below</Paragraph>,
          ]}
        />

        <Input
          name="webhook"
          label="Webhook URL"
          value={webhook}
          placeholder="e.g. https://hooks.slack.com/services/T03BC430S/CF8QCE"
          onChange={handleChange}
          error={!!webhookErr}
          helper={webhookErr && webhookErr.message}
          disabled={disabled}
          autoFocus
        />
        <Input
          name="channel"
          disabled={disabled}
          value={channel}
          label="Channel"
          error={!!channelErr}
          helper={channelErr && channelErr.message}
          onChange={handleChange}
          placeholder="e.g. #general"
        />
        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  webhook: string,
  channel: string,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Slack,
) => {
  if (!webhook || !channel) return true;
  if (!integration) return false;

  if (webhook !== integration.webhook_url || channel !== integration.channel_name) {
    return false;
  }

  // Check if integration layout has changed
  return !hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout);
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
